import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiEdit2, FiArrowLeft, FiUsers, FiActivity, FiFileText, FiClock, FiCheckCircle, FiXCircle, FiTrash2 } from 'react-icons/fi';
import { exams as examsApi } from '../../api';
import toast from 'react-hot-toast';

export default function ExamDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    examsApi.getOne(id).then(r => setExam(r.data.data)).catch(() => toast.error('Failed to load exam')).finally(() => setLoading(false));
  }, [id]);

  const handlePublish = async () => {
    const newStatus = exam.status === 'published' ? 'draft' : 'published';
    setPublishing(true);
    try {
      const res = await examsApi.publish(id, newStatus);
      setExam(res.data.data);
      toast.success(`Exam ${newStatus === 'published' ? 'published' : 'unpublished'}`);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setPublishing(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this exam? All questions and results will be lost.')) return;
    try { await examsApi.delete(id); toast.success('Exam deleted'); navigate('/admin/exams'); }
    catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-navy/20 border-t-navy rounded-full animate-spin" /></div>;
  if (!exam) return <div className="text-center py-20 text-gray-400">Exam not found</div>;

  const STATUS_CONFIG = {
    draft: { color: 'bg-gray-100 text-gray-700', label: 'Draft' },
    published: { color: 'bg-emerald-100 text-emerald-700', label: 'Published' },
    active: { color: 'bg-blue-100 text-blue-700', label: 'Active' },
    completed: { color: 'bg-purple-100 text-purple-700', label: 'Completed' },
    archived: { color: 'bg-orange-100 text-orange-700', label: 'Archived' },
  };
  const sc = STATUS_CONFIG[exam.status] || STATUS_CONFIG.draft;

  return (
    <div className="space-y-5 max-w-4xl animate-fade-in">
      <div className="flex items-start gap-3">
        <Link to="/admin/exams" className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 mt-0.5"><FiArrowLeft size={18} /></Link>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-black text-gray-900">{exam.title}</h1>
            <span className={`text-sm font-bold px-3 py-1 rounded-full capitalize ${sc.color}`}>{sc.label}</span>
          </div>
          {exam.description && <p className="text-gray-500 text-sm mt-1">{exam.description}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to={`/admin/exams/${id}/questions`} className="btn-secondary text-sm py-2">
            <FiFileText size={14} /> Questions ({exam.question_count || 0})
          </Link>
          <Link to={`/admin/exams/${id}/monitor`} className="btn-secondary text-sm py-2">
            <FiActivity size={14} /> Monitor
          </Link>
          <button onClick={handlePublish} disabled={publishing} className={exam.status === 'published' ? 'btn-secondary text-sm py-2' : 'btn-success text-sm py-2'}>
            {publishing ? <span className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin" /> : (exam.status === 'published' ? <FiXCircle size={14} /> : <FiCheckCircle size={14} />)}
            {exam.status === 'published' ? 'Unpublish' : 'Publish'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: FiFileText, label: 'Questions', value: `${exam.question_count || 0} / ${exam.total_questions}` },
          { icon: FiClock, label: 'Duration', value: `${exam.duration_minutes} min` },
          { icon: FiUsers, label: 'Registered', value: exam.registered_count || 0 },
          { icon: FiActivity, label: 'Completed', value: exam.completed_count || 0 },
        ].map(({ icon: Icon, label, value }) => (
          <div key={label} className="card p-4">
            <Icon size={20} className="text-navy mb-2" />
            <p className="text-xl font-black text-gray-900">{value}</p>
            <p className="text-xs text-gray-500">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card p-5">
          <h3 className="font-bold text-gray-900 mb-3">Exam Settings</h3>
          <dl className="space-y-2.5 text-sm">
            {[
              ['Category', exam.category || 'Not set'],
              ['Passing Marks', `${exam.passing_marks}%`],
              ['Max Marks', exam.max_marks],
              ['Marks/Question', exam.marks_per_question],
              ['Negative Marks', exam.negative_marks],
              ['Max Attempts', exam.max_attempts],
              ['Display Mode', exam.display_mode?.replace('_', ' ')],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between py-1 border-b border-gray-50">
                <dt className="text-gray-500">{k}</dt>
                <dd className="font-medium text-gray-900 capitalize">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="card p-5">
          <h3 className="font-bold text-gray-900 mb-3">Features</h3>
          <dl className="space-y-2.5 text-sm">
            {[
              ['Randomize Questions', exam.is_randomized],
              ['Randomize Options', exam.randomize_options],
              ['Allow Review', exam.allow_review],
              ['Show Results Immediately', exam.show_result_immediately],
              ['Proctoring Enabled', exam.proctoring_enabled],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between py-1 border-b border-gray-50">
                <dt className="text-gray-500">{k}</dt>
                <dd className={`font-bold ${v ? 'text-emerald-600' : 'text-red-400'}`}>{v ? '✓ Yes' : '✗ No'}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-5 pt-4 border-t border-gray-100">
            <button onClick={handleDelete} className="btn-danger w-full text-sm py-2">
              <FiTrash2 size={14} /> Delete Exam
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
